import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowRight, Loader2, TriangleAlert } from 'lucide-react';
import { useEffect, useState } from 'react';
import { TowarPicker, type Towar } from '@/components/PanelEdycji';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { useToast } from '@/components/ui/toast';
import { ApiError } from '@/lib/api';
import { useI18n } from '@/lib/i18n';
import { listItems } from '@/lib/items-api';
import { ROZMIAR_STRONY_TOWAROW } from '@/lib/lista-towarow';
import { podgladPodmiany, podmienIndeks, przeniesLimity, type PodmianaPayload } from '@/lib/planogram-api';

/**
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *  WYMIANA JEDNEGO INDEKSU NA INNY W PLANOGRAMIE.
 *
 *  ⚠️⚠️ ZAMÓWIONE 09.09.2026: „czasem pojawia się opcja wymiany asortymentu w danym bębnie […]
 *  wypierdalamy całkiem jeden artykuł i w te same szuflady pakujemy inny — w tej chwili trzeba to
 *  robić ręcznie". Zmierzone na produkcji: największy indeks siedzi w 126 przegródkach, czyli
 *  ręcznie to 126 zaznaczeń w kilku kolumnach.
 *
 *  ⚠️⚠️ PRZEGRÓDKI LICZY SERWER, NIE TO OKNO. Wysyłamy dwa indeksy; listę przegródek i skutki
 *  (ile stanu przepadnie, które reguły jednolitości spadną) oddaje podgląd. Policzenie tego drugi
 *  raz tutaj znaczyłoby, że ostrzeżenie i zapis mogą się rozjechać — a rozjazd na tej tabeli to
 *  utracony stan magazynowy w bazie klienta.
 *
 *  ⚠️⚠️ TRZY RZECZY MUSZĄ PAŚĆ PRZED KLIKNIĘCIEM, i to jest cały powód, dla którego to okno
 *  istnieje osobno, a nie jako pozycja w panelu edycji:
 *    • ILE STANU PRZEPADNIE — podmiana to `DELETE` + `INSERT`, więc `QTYCURRENT` idzie do zera;
 *    • CO ZOSTAJE NA STARYM INDEKSIE — limity i ograniczenia dostępu, których ta operacja NIE
 *      rusza (rozstrzygnięte z użytkownikiem: przeniesienie osobnym przyciskiem);
 *    • GDZIE TO LEŻY — rozbicie per bęben, z możliwością odznaczenia bębna.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 */
export function PodmianaIndeksu({
  groupId,
  machineId,
  obecne,
  onZamknij,
}: {
  groupId: string;
  machineId: string;
  /** Indeksy LEŻĄCE DZIŚ w tym automacie, z liczbą przegródek — z tego wybiera się wycofywany. */
  obecne: { id: string; name: string; itemNumber: string; ile: number }[];
  onZamknij: () => void;
}) {
  const { t } = useI18n();
  const toast = useToast();
  const qc = useQueryClient();

  const [zItemId, setZItemId] = useState<string | null>(null);
  /**
   * CO WCHODZI W TE PRZEGRÓDKI — TRZY STANY, NIE DWA.
   *
   * ⚠️⚠️ `null` ZNACZY „NIC NIE WYBRANO", a `{ rodzaj: 'PUSTE' }` — „opróżnij". Zlanie tych
   * dwóch w jeden `Towar | null` (jak w panelu edycji) znaczyłoby, że nie da się odróżnić
   * niewypełnionego formularza od świadomej decyzji o wyczyszczeniu — a jeden z tych stanów
   * pozwala zlecić operację, a drugi nie.
   */
  const [cel, setCel] = useState<{ rodzaj: 'TOWAR'; towar: Towar } | { rodzaj: 'PUSTE' } | null>(null);
  const [szukaj, setSzukaj] = useState('');
  /** Bębny ODZNACZONE przez operatora — domyślnie żaden, czyli cały automat. */
  const [pominiete, setPominiete] = useState<ReadonlySet<number>>(new Set());
  const [maks, setMaks] = useState('');
  const [wyd, setWyd] = useState('');
  const [potwierdzam, setPotwierdzam] = useState(false);

  /** ⚠️ Zmiana indeksu wycofywanego zeruje odznaczenia — bębny są inne dla innego towaru. */
  useEffect(() => setPominiete(new Set()), [zItemId]);

  /**
   * ⚠️⚠️ ZMIANA TEGO, CO POJEDZIE, COFA POTWIERDZENIE. Zdanie z liczbami pada po pierwszym
   * kliknięciu — gdyby podmiana indeksu albo ilości nie cofała drugiego kroku, przycisk wysłałby
   * operację, której nikt w tej postaci nie przeczytał.
   */
  useEffect(() => setPotwierdzam(false), [zItemId, cel, maks, wyd]);

  const towary = useQuery({
    queryKey: ['items', 'podmiana-picker', groupId, szukaj],
    queryFn: () =>
      listItems({
        groupId,
        ...(szukaj.trim() === '' ? {} : { search: szukaj.trim() }),
        status: 'active',
        duplicateSupplierPart: false,
        page: 1,
        pageSize: ROZMIAR_STRONY_TOWAROW,
        sort: 'name',
        dir: 'asc',
      }),
    enabled: Boolean(groupId),
  });

  const czyscimy = cel?.rodzaj === 'PUSTE';
  const liczba = (s: string) => (s.trim() === '' ? undefined : Number(s));
  /**
   * ⚠️ PRZY OPRÓŻNIANIU NIE WYSYŁAMY ILOŚCI — nie powstaje żaden wiersz, więc nie ma czego
   * opisywać, a serwer takie żądanie odrzuca (`PodmianaIndeksuSchema`). Ekran ma nie składać
   * żądania, o którym z góry wie, że zostanie odesłane.
   */
  const nadpisanie =
    czyscimy || liczba(maks) === undefined || liczba(wyd) === undefined
      ? {}
      : { qtyMax: liczba(maks), qtyDisp: liczba(wyd) };

  const payload: PodmianaPayload | null =
    zItemId === null || cel === null
      ? null
      : {
          groupId,
          machineId,
          zItemId,
          naItemId: cel.rodzaj === 'TOWAR' ? cel.towar.id : null,
          ...nadpisanie,
        };

  /**
   * ⚠️ PODGLĄD LICZY SIĘ BEZ FILTRA BĘBNÓW, a odznaczanie stosujemy dopiero przy zapisie —
   * inaczej odznaczenie ostatniego bębna kasowałoby podgląd i nie dałoby się go przywrócić.
   * Liczby przy odznaczonym bębnie odejmujemy tutaj, z rozbicia, które podgląd już oddał.
   */
  const podglad = useQuery({
    queryKey: ['podmiana-podglad', payload],
    queryFn: () => podgladPodmiany(payload!),
    enabled: payload !== null,
    retry: false,
  });

  const bebnyWybrane = (podglad.data?.bebny ?? []).filter((b) => !pominiete.has(b.picNumber));
  const przegrodekWybranych = bebnyWybrane.reduce((a, b) => a + b.przegrodek, 0);
  /** ⚠️ Wszystkie bębny odznaczone = nie ma czego podmieniać; przycisk musi być wtedy martwy. */
  const nicNieZostalo = podglad.data !== undefined && przegrodekWybranych === 0;

  const podmien = useMutation({
    mutationFn: () =>
      podmienIndeks({
        ...payload!,
        ...(pominiete.size === 0 ? {} : { picNumbers: bebnyWybrane.map((b) => b.picNumber) }),
      }),
    onSuccess: (w) => {
      toast.success(
        (w.naNazwa === null ? t('podm.zleconaPuste') : t('podm.zlecona'))
          .replace('{z}', w.zNazwa)
          .replace('{na}', w.naNazwa ?? '')
          .replace('{n}', String(w.przegrodki)),
      );
      void qc.invalidateQueries({ queryKey: ['planogram'] });
      void qc.invalidateQueries({ queryKey: ['changes'] });
      onZamknij();
    },
    /**
     * ⚠️⚠️ TYLKO `4xx` JEST DOWODEM, ŻE NIC NIE POSZŁO — i to jest poprawka awarii z 10.09.2026,
     * zmierzonej na małpkomacie: podmiana wykonała się w bazie automatu (6 wierszy, 11:09:01),
     * a okno pokazało „Nie udało się wymienić indeksu", bo odpowiedź serwera nie przeszła
     * walidacji schematu PO zatwierdzeniu operacji.
     *
     * Serwer odmawia z `4xx` i WŁASNYM zdaniem („Nic do zrobienia…", „za dużo zmian…") — to
     * pokazujemy dosłownie. Wszystko inne (`5xx`, zerwana sieć, nieczytelna odpowiedź) znaczy
     * NIE WIEM: operacja mogła już stać w rejestrze zmian, więc ekran mówi, gdzie sprawdzić,
     * zamiast wysyłać operatora po drugie zlecenie tej samej podmiany.
     */
    onError: (e: Error) =>
      e instanceof ApiError && e.status < 500
        ? toast.error(e.message)
        : toast.toast(t('podm.nieWiadomo'), 'info'),
  });

  /**
   * PRZENIESIENIE LIMITÓW — OSOBNA OPERACJA, OSOBNE KLIKNIĘCIE.
   *
   * ⚠️⚠️ ROZSTRZYGNIĘTE Z UŻYTKOWNIKIEM: podmiana w planogramie limitów NIE rusza, a ich
   * przeniesienie idzie osobnym przyciskiem. To zmiana UPRAWNIEŃ LUDZI, nie asortymentu —
   * i dlatego nie doklejamy jej do „Wymień", nawet gdy operator zrobi jedno po drugim.
   *
   * ⚠️ PO PRZENIESIENIU ODŚWIEŻAMY PODGLĄD, żeby licznik „zostaje na starym" spadł do zera.
   * Ostrzeżenie wiszące po wykonaniu operacji uczyłoby, że nic się nie stało.
   */
  const limity = useMutation({
    /**
     * ⚠️ `cel` MUSI BYĆ TOWAREM — przy opróżnianiu nie ma dokąd przenieść, a przycisk jest wtedy
     * ukryty. Wykrzyknik jest tu wnioskiem z tego, że przycisk bez towaru się nie renderuje.
     */
    mutationFn: () =>
      przeniesLimity({
        groupId,
        zItemId: zItemId!,
        naItemId: cel?.rodzaj === 'TOWAR' ? cel.towar.id : '',
      }),
    onSuccess: (w) => {
      toast.success(
        t('podm.limityPrzeniesione')
          .replace('{limitow}', String(w.limitow))
          .replace('{ograniczen}', String(w.ograniczen)),
      );
      void podglad.refetch();
      void qc.invalidateQueries({ queryKey: ['changes'] });
    },
    /**
     * ⚠️ Kolizja wraca jako błąd Z TREŚCIĄ — wymienia cele, więc pokażemy ją dosłownie.
     * ⚠️⚠️ Ta sama zasada co przy podmianie: poza `4xx` nie wiemy, czy przeniesienie poszło.
     */
    onError: (e: Error) =>
      e instanceof ApiError && e.status < 500
        ? toast.error(e.message)
        : toast.toast(t('podm.nieWiadomo'), 'info'),
  });

  const stary = obecne.find((x) => x.id === zItemId) ?? null;

  return (
    <Dialog open onOpenChange={(o) => !o && onZamknij()}>
      {/* ⚠️ `title` JEST WYMAGANY PROPSEM — nagłówek modala składa `DialogContent`, nie my. */}
      <DialogContent className="max-w-xl" title={t('podm.tytul')} description={t('podm.opis')}>

        <div className="space-y-3">
          {/*
            ⚠️ WYCOFYWANY WYBIERAMY Z TEGO, CO LEŻY W AUTOMACIE — nie z katalogu wyspy. Lista,
            w której 90% pozycji nie ma czego podmienić, zamienia wybór w zgadywankę; ta sama
            zasada, co przy szukajce towaru w bębnie.
          */}
          <label className="flex flex-col gap-1 text-xs text-muted-foreground">
            {t('podm.zIndeksu')}
            <select
              className="h-9 rounded-md border border-input bg-background px-2 text-sm text-foreground"
              value={zItemId ?? ''}
              onChange={(e) => setZItemId(e.target.value === '' ? null : e.target.value)}
            >
              <option value="">{t('podm.wybierz')}</option>
              {obecne.map((x) => (
                <option key={x.id} value={x.id}>
                  {x.name} · {t('podm.przegrodek').replace('{n}', String(x.ile))}
                </option>
              ))}
            </select>
          </label>

          <div className="flex items-center gap-2 text-muted-foreground">
            <ArrowRight className="size-4" />
            <span className="text-xs">{t('podm.wTeSame')}</span>
          </div>

          <TowarPicker
            szukaj={szukaj}
            onSzukaj={setSzukaj}
            wybrany={cel?.rodzaj === 'TOWAR' ? cel.towar : null}
            onWybierz={(x) => setCel(x === null ? null : { rodzaj: 'TOWAR', towar: x })}
            wyniki={towary.data?.rows ?? []}
            razem={towary.data?.total ?? 0}
            ladowanie={towary.isPending}
            /**
             * ⚠️⚠️ ZAMÓWIONE 09.09.2026: „dodajmy jeszcze opcję, by po prostu wyczyścić na puste
             * przegrody — może to być jako pierwsza rzecz na liście". Stąd PIERWSZA pozycja, a nie
             * osobny przycisk: dla człowieka to ta sama decyzja („co ma tu leżeć"), tylko odpowiedź
             * brzmi „nic".
             */
            pozycjaPusta={{
              etykieta: t('podm.pusta'),
              wybrana: czyscimy,
              onWybierz: () => setCel({ rodzaj: 'PUSTE' }),
            }}
          />

          {/*
            ⚠️ PUSTE = KOPIUJEMY PER PRZEGRÓDKA, i tak jest domyślnie (rozstrzygnięte
            z użytkownikiem): to ta sama fizyczna przegródka, więc pojemność się nie zmienia.
            Nadpisanie jest dla nowego towaru w innym opakowaniu i działa na WSZYSTKIE naraz.
          */}
          {/*
            ⚠️ PRZY OPRÓŻNIANIU POLA ILOŚCI ZNIKAJĄ, nie są tylko wyszarzone: nie powstaje żaden
            wiersz, więc pojemność nie ma czego opisywać. Wyszarzone pole pytałoby o liczbę, która
            nigdzie nie trafia — a serwer takie żądanie wprost odrzuca.
          */}
          <div className={czyscimy ? 'hidden' : 'flex gap-2'}>
            <label className="flex flex-1 flex-col gap-1 text-xs text-muted-foreground">
              {t('podm.maks')}
              <Input
                type="number"
                min={0}
                value={maks}
                onChange={(e) => setMaks(e.target.value)}
                placeholder={t('podm.zeStarego')}
                className="h-9"
              />
            </label>
            <label className="flex flex-1 flex-col gap-1 text-xs text-muted-foreground">
              {t('podm.wyd')}
              <Input
                type="number"
                min={0}
                value={wyd}
                onChange={(e) => setWyd(e.target.value)}
                placeholder={t('podm.zeStarego')}
                className="h-9"
              />
            </label>
          </div>

          {podglad.isPending && payload !== null && (
            <p className="text-xs text-muted-foreground">{t('common.loading')}</p>
          )}
          {podglad.isError && (
            <p className="text-xs text-destructive">
              {podglad.error instanceof ApiError ? podglad.error.message : t('podm.blad')}
            </p>
          )}

          {podglad.data !== undefined && (
            <div className="space-y-2 rounded-md border border-border bg-muted/20 p-3 text-xs">
              {/*
                ⚠️⚠️ ROZBICIE PER BĘBEN Z ODZNACZANIEM — rozstrzygnięte z użytkownikiem. Zgłoszenie
                mówiło i „w danym bębnie", i „całkiem jeden artykuł", więc ekran pozwala wybrać,
                zamiast zgadywać, o którą z tych dwóch operacji chodzi.
              */}
              <p className="font-medium text-foreground">
                {t('podm.gdzieLezy').replace('{n}', String(podglad.data.przegrodki))}
              </p>
              <div className="flex flex-wrap gap-3">
                {podglad.data.bebny.map((b) => (
                  <label key={b.picNumber} className="flex items-center gap-1.5">
                    <input
                      type="checkbox"
                      checked={!pominiete.has(b.picNumber)}
                      onChange={(e) =>
                        setPominiete((p) => {
                          const n = new Set(p);
                          if (e.target.checked) n.delete(b.picNumber);
                          else n.add(b.picNumber);
                          return n;
                        })
                      }
                    />
                    {t('podm.beben').replace('{pic}', String(b.picNumber)).replace('{n}', String(b.przegrodek))}
                  </label>
                ))}
              </div>

              {/*
                ⚠️⚠️ STAN PRZEPADA I EKRAN MUSI TO POWIEDZIEĆ LICZBĄ. Podmiana to `DELETE`+`INSERT`
                w SPS, więc `QTYCURRENT` idzie do zera — towar wyjmuje się z bębna fizycznie.
                „Zniknęło 123 szt." musi mieć odpowiedź PRZED kliknięciem, nie po miesiącu.
              */}
              {podglad.data.stockLost > 0 && (
                <p className="flex items-start gap-1.5 text-[var(--warning)]">
                  <TriangleAlert className="mt-0.5 size-3.5 shrink-0" />
                  {t('podm.stanPrzepadnie').replace('{n}', String(podglad.data.stockLost))}
                </p>
              )}

              {/*
                ⚠️⚠️ CO ZOSTAJE NA STARYM INDEKSIE. Na poziomie TOWARU jest tego mało (zmierzone
                na czterech bazach: 0, 2, 1, 0), ale gdy jest — limit zostawiony na wycofanym
                indeksie znaczy nowy towar BEZ ŻADNEGO limitu, i to jest cicha strata, jeśli
                ekran o niej nie powie. Obok stoi przycisk, który je przenosi.
              */}
              {(podglad.data.limitow > 0 || podglad.data.ograniczen > 0) && (
                <div className="space-y-1.5">
                  <p className="flex items-start gap-1.5 text-[var(--warning)]">
                    <TriangleAlert className="mt-0.5 size-3.5 shrink-0" />
                    {t('podm.limityZostaja')
                      .replace('{limitow}', String(podglad.data.limitow))
                      .replace('{ograniczen}', String(podglad.data.ograniczen))}
                  </p>
                  {/*
                    ⚠️⚠️ PRZYCISK PRZY OSTRZEŻENIU, nie w stopce okna. Stopka należy do PODMIANY;
                    to jest DRUGA, niezależna operacja i ma stać tam, gdzie pada zdanie, które
                    tłumaczy, po co jest. Dwie decyzje, dwa ślady w rejestrze zmian.

                    ⚠️ DZIAŁA NIEZALEŻNIE OD PODMIANY — można kliknąć przed nią, po niej albo
                    bez niej. Limity nie mają numeru automatu, więc nie ma czego z podmianą wiązać.
                  */}
                  {/*
                    ⚠️⚠️ PRZY OPRÓŻNIANIU NIE MA DOKĄD PRZENIEŚĆ — nie ma nowego indeksu. Przycisk
                    musi wtedy zniknąć, a ostrzeżenie o limitach ZOSTAĆ: wycofanie towaru bez
                    zastąpienia też zostawia jego limity wiszące na indeksie, którego nie ma w żadnej
                    przegródce. To wtedy jedyne miejsce, w którym ktoś się o tym dowie.
                  */}
                  <Button
                    size="sm"
                    variant="outline"
                    className={czyscimy ? 'hidden' : 'h-7'}
                    disabled={limity.isPending}
                    onClick={() => limity.mutate()}
                    title={t('podm.przeniesLimityOpis')}
                  >
                    {limity.isPending && <Loader2 className="size-3.5 animate-spin" />}
                    {t('podm.przeniesLimity')}
                  </Button>
                </div>
              )}

              {podglad.data.uniformRulesDropped.length > 0 && (
                <p className="text-muted-foreground">
                  {t('podm.jednolitosc').replace('{kolumny}', podglad.data.uniformRulesDropped.join(', '))}
                </p>
              )}

              {nicNieZostalo && <p className="text-destructive">{t('podm.nicNieZostalo')}</p>}
            </div>
          )}

          {/*
            ⚠️⚠️ ZDANIE O OPERACJI STOI W TREŚCI OKNA, NIE W ETYKIECIE PRZYCISKU — i to jest
            POPRAWKA PO ZGŁOSZENIU Z 10.09.2026: „informacja o podmianie nie zmieściła się na
            ekranie (czerwony pasek) i w sumie nie wiemy teraz czy zadziałało czy nie".
            Tym czerwonym paskiem BYŁ przycisk zatwierdzenia: całe zdanie „Wymień «X» na «Y»
            w 6 przegr." siedziało w jego etykiecie. Przyciski mają `whitespace-nowrap`, więc
            etykieta z dwiema nazwami towarów była szersza od modala; `justify-end` wypchnął
            nadmiar w LEWO, za krawędź okna, a zawinięty na własny wiersz pasek stanął pod
            „Anuluj" i przeczytał się jak komunikat o WYNIKU operacji, której jeszcze nie było.
            Zdanie zawija się tylko w treści okna — w przycisku nie ma na nie miejsca i nigdy nie
            będzie, bo szerokość modala nie zależy od długości nazw towarów u klienta.

            ⚠️ LICZBA JEST Z WYBRANYCH BĘBNÓW (`przegrodekWybranych`), nie z całości podglądu —
            to ona pojedzie w żądaniu, gdy operator odznaczy bęben.
          */}
          {potwierdzam && (
            <p
              role="status"
              className="rounded-md border border-border bg-muted/40 p-3 text-sm font-medium text-foreground"
            >
              {(czyscimy ? t('podm.potwierdzPuste') : t('podm.potwierdz'))
                .replace('{z}', stary?.name ?? '?')
                .replace('{na}', cel?.rodzaj === 'TOWAR' ? cel.towar.name : '')
                .replace('{n}', String(przegrodekWybranych))}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onZamknij} disabled={podmien.isPending}>
            {t('common.cancel')}
          </Button>
          {/*
            ⚠️ DWA KLIKNIĘCIA, NIE JEDNO. Operacja jest nieodwracalna po stronie stanu
            magazynowego, a liczba przegródek bywa trzycyfrowa — pierwsze kliknięcie odsłania
            zdanie z liczbami, drugie wysyła.
          */}
          {!potwierdzam ? (
            <Button
              variant="destructive"
              disabled={podglad.data === undefined || nicNieZostalo}
              onClick={() => setPotwierdzam(true)}
            >
              {t('podm.dalej')}
            </Button>
          ) : (
            <Button variant="destructive" disabled={podmien.isPending} onClick={() => podmien.mutate()}>
              {podmien.isPending && <Loader2 className="animate-spin" />}
              {/*
                ⚠️⚠️ JEDNO SŁOWO, BEZ NAZW TOWARÓW I BEZ LICZB — zdanie o operacji stoi wyżej,
                w treści okna. Etykieta z nazwami dwóch indeksów wyjeżdżała poza modal
                (zgłoszenie 10.09.2026); nazwy towarów u klienta bywają czterdziestoznakowe,
                a szerokość okna od nich nie zależy.

                ⚠️ Inna etykieta przy opróżnianiu — „Wymień" nie opisuje wyjęcia bez zastąpienia.
              */}
              {czyscimy ? t('podm.wykonajPuste') : t('podm.wykonaj')}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
